import React, { useEffect, useMemo } from 'react';

import {
  IconButton,
  SidePage,
  LegacyText,
  LegacyBox,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Menu,
  MenuItem,
  AssignmentIcon,
  RestoreIcon,
} from '@gogame/components';
import { useMountedState, useProcessAsync } from '@gogame/hooks-ts';
import { DateValueFormatter, useMonthFormat } from '@gogame/intl-ts';
import { ScreenLink, useScreenModalV2 } from '@gogame/screens';
import { useSocket } from '@gogame/sockets';

import Status from 'scenes/MarketingPlanning/MarketingPlanningGrid/components/Status';
import { EVENT_RELOAD_GRID } from 'scenes/MarketingPlanning/MarketingPlanningGrid/constants';
import { useLoadMarketingPlanningHistory } from 'scenes/MarketingPlanning/MarketingPlanningGrid/hooks';
import { useRestoreMarketingPlan } from 'scenes/MarketingPlanning/MarketingPlanningGrid/hooks';
import SummaryByDirectorsDialog from 'scenes/MarketingPlanning/components/SummaryByDirectorsDialog';
import SummaryByManagersDialog from 'scenes/MarketingPlanning/components/SummaryByManagersDialog';
import SummaryByOwnersDialog from 'scenes/MarketingPlanning/components/SummaryByOwnersDialog';
import { marketingPlanningPreviewLink } from 'scenes/MarketingPlanning/navigation';

const MarketingPlanningHistory = ({ year, month, onClose, onExited, open }) => {
  const [data, setData] = useMountedState({ history: [], meta: {} });
  const [loading, loadingWrapper] = useProcessAsync();
  const loadView = useLoadMarketingPlanningHistory(loadingWrapper);
  const restoreMarketingPlan = useRestoreMarketingPlan(loadingWrapper);
  const showSummaryByOwnersDialog = useScreenModalV2(SummaryByOwnersDialog);
  const showSummaryByManagersDialog = useScreenModalV2(SummaryByManagersDialog);
  const showSummaryByDirectorsDialog = useScreenModalV2(
    SummaryByDirectorsDialog
  );
  const monthYearFormat = useMonthFormat();
  const socket = useSocket();

  const loadData = () => {
    return loadView({ year, month }).then(setData);
  };

  useEffect(() => {
    loadData();
  }, [year, month]);

  useEffect(() => {
    socket.on(EVENT_RELOAD_GRID, loadData);
  }, [socket]);

  const name = useMemo(() => {
    return monthYearFormat({ year, month });
  }, [year, month]);

  const handleSummaryByOwners = (history) => () => {
    showSummaryByOwnersDialog({
      year: history.year,
      month: history.month,
      version: history.version,
    });
  };
  const handleSummaryByManagers = (history) => () => {
    showSummaryByManagersDialog({
      year: history.year,
      month: history.month,
      version: history.version,
    });
  };

  const handleSummaryByDirectors = (history) => () => {
    showSummaryByDirectorsDialog({
      year: history.year,
      month: history.month,
      version: history.version,
    });
  };

  const handleRestoreHistory = (history) => () => {
    restoreMarketingPlan(history);
  };

  return (
    <SidePage
      backdrop={false}
      onClose={onClose}
      onExited={onExited}
      loading={loading}
      open={open}
      size={'lg'}
    >
      <SidePage.Title title={'Monthly Plan history'} subTitle={name} />
      <SidePage.Content padding={0}>
        <LegacyText variant={'subtitle1'} style={{ padding: '16px 24px' }}>
          History
        </LegacyText>

        <TableContainer>
          <Table name={'historyTable'}>
            <TableHead>
              <TableRow>
                <TableCell columnTitle={'Version'}>Version</TableCell>
                <TableCell columnTitle={'Status'} width={'170px'}>
                  Status
                </TableCell>
                <TableCell columnTitle={'Comment'}>Comment</TableCell>
                <TableCell columnTitle={'Created by'}>Created by</TableCell>
                <TableCell columnTitle={'Created'}>Created</TableCell>
                <TableCell columnTitle={'Actions'}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.history.map((row, i) => (
                <TableRow key={i}>
                  <TableCell size={'small'}>
                    <LegacyText size={'13px'}>
                      <ScreenLink
                        link={marketingPlanningPreviewLink}
                        pathParams={{
                          year: row?.year,
                          month: row?.month,
                        }}
                        searchParams={{
                          version: row?.version,
                        }}
                        style={{
                          textDecoration: 'underline',
                          whiteSpace: 'nowrap',
                        }}
                        target={'_blank'}
                      >
                        {row?.viewVersion}
                      </ScreenLink>
                    </LegacyText>
                  </TableCell>
                  <TableCell size={'small'}>
                    <div style={{ display: 'flex' }}>
                      <Status id={row?.status?.id}>{row?.status?.name}</Status>
                    </div>
                  </TableCell>
                  <TableCell size={'small'}>
                    <LegacyText size={'13px'}>{row?.comment}</LegacyText>
                  </TableCell>
                  <TableCell size={'small'}>
                    <LegacyText size={'13px'}>
                      {row?.lastUpdatedBy?.name}
                    </LegacyText>
                  </TableCell>
                  <TableCell size={'small'}>
                    <LegacyText size={'13px'}>
                      <DateValueFormatter
                        value={row?.dateCreated}
                        template={'LLL'}
                        timeZone={'local'}
                      />
                    </LegacyText>
                  </TableCell>
                  <TableCell size={'small'}>
                    <LegacyBox>
                      <Menu
                        anchor={
                          <IconButton
                            icon={<AssignmentIcon />}
                            tooltip={'View summary'}
                          />
                        }
                        anchorOrigin={{
                          vertical: 'center',
                          horizontal: 'left',
                        }}
                        transformOrigin={{
                          vertical: 'center',
                          horizontal: 'right',
                        }}
                      >
                        <MenuItem onClick={handleSummaryByOwners(row)}>
                          Summary by owners
                        </MenuItem>
                        <MenuItem onClick={handleSummaryByManagers(row)}>
                          Summary by managers
                        </MenuItem>
                        <MenuItem onClick={handleSummaryByDirectors(row)}>
                          Summary by directors
                        </MenuItem>
                      </Menu>
                      <IconButton
                        icon={<RestoreIcon />}
                        tooltip={'Restore'}
                        disabled={i === 0 && !data.meta.hasUnsavedChanges}
                        onClick={handleRestoreHistory(row)}
                      />
                    </LegacyBox>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </SidePage.Content>
      <SidePage.Actions>
        <SidePage.CloseButton />
      </SidePage.Actions>
    </SidePage>
  );
};

export default MarketingPlanningHistory;
